// Create this file: frontend/src/utils/campaignAI.js
/**
 * Shared AI Campaign Analysis Utility
 * Used by both Dashboard and Campaign Graveyard to ensure consistent results
 */

export const analyzeCampaignWaste = (campaigns) => {
  const campaignList = Array.isArray(campaigns) ? campaigns : [];
  
  let totalWaste = 0;
  let problematicCampaigns = 0;
  let connectedAccounts = 0;
  
  const analysisResults = campaignList.map(campaign => {
    // Platform detection
    const platform = campaign.platform?.toLowerCase() || 'facebook';
    
    // Metric parsing
    const ctr = parseFloat(campaign.ctr) || 0;
    const spend = parseFloat(campaign.spend) || 0;
    const clicks = parseInt(campaign.clicks) || 0;
    const impressions = parseInt(campaign.impressions) || 0;
    const cpc = parseFloat(campaign.cpc) || 0;
    const frequency = parseFloat(campaign.frequency) || 0;
    const reach = parseInt(campaign.reach) || 0;

    // Status detection
    let actual_status = campaign.status?.toLowerCase() || 
                      campaign.delivery_status?.toLowerCase() || 
                      campaign.effective_status?.toLowerCase() || 
                      'unknown';

    // Default values
    let causeOfDeath = 'Performance Review';
    let severity = 'INVESTIGATING';
    let diagnosis = 'Analyzing performance patterns with advanced algorithms...';
    let recommendation = 'Running diagnostic protocols...';
    let actualWaste = 0;
    let isProblematic = false;
    let confidence = Math.min(95, Math.max(10, (spend / 100) * 15 + (impressions / 1000) * 5));

    // Platform-specific thresholds (same as Campaign Graveyard)
    const platformConfig = {
      'facebook': { goodCTR: 1.5, okCTR: 0.8, maxFreq: 3.0, maxReach: 100000 },
      'google': { goodCTR: 2.0, okCTR: 1.2, maxFreq: 2.5, maxReach: 150000 },
      'tiktok': { goodCTR: 1.2, okCTR: 0.6, maxFreq: 4.0, maxReach: 80000 },
      'linkedin': { goodCTR: 0.8, okCTR: 0.4, maxFreq: 2.0, maxReach: 50000 },
      'twitter': { goodCTR: 1.0, okCTR: 0.5, maxFreq: 3.5, maxReach: 75000 }
    };

    const config = platformConfig[platform] || platformConfig['facebook'];
    const MIN_SPEND = platform === 'linkedin' ? 100 : 50;

    // AI-powered analysis logic (SAME as Campaign Graveyard)
    if (actual_status === 'paused') {
      causeOfDeath = 'Manual Intervention';
      severity = 'PAUSED';
      diagnosis = `Campaign manually paused. Last performance: ${ctr.toFixed(2)}% CTR, ${cpc.toFixed(2)} CPC`;
      recommendation = 'Review pause reason. Consider reactivating if metrics were healthy.';
      isProblematic = false;
    } 
    else if (actual_status === 'archived' || actual_status === 'deleted') {
      causeOfDeath = 'Campaign Archived';
      severity = 'ARCHIVED';
      diagnosis = 'Campaign reached end date or was manually archived.';
      recommendation = 'Analyze final performance data for learnings.';
      isProblematic = false;
    }
    else if (actual_status === 'pending' || actual_status === 'in_review') {
      causeOfDeath = 'Platform Review';
      severity = 'PENDING';
      diagnosis = 'Campaign awaiting platform approval.';
      recommendation = 'Monitor approval status and check platform policies.';
      isProblematic = false;
    }
    // AI PERFORMANCE ANALYSIS
    else if (frequency > config.maxFreq) {
      causeOfDeath = 'Ad Fatigue Detected';
      severity = 'CRITICAL';
      diagnosis = `High frequency (${frequency.toFixed(1)}x) indicates audience burnout.`;
      recommendation = 'Expand audience or rotate creatives immediately.';
      actualWaste = spend * 0.4; // 40% waste for ad fatigue
      isProblematic = true;
      confidence = 88;
    }
    else if (reach > config.maxReach && ctr < config.okCTR) {
      causeOfDeath = 'Overbroad Targeting';
      severity = 'BLEEDING';
      diagnosis = `Reaching ${reach.toLocaleString()} people with low engagement (CTR: ${ctr.toFixed(2)}%).`;
      recommendation = 'Narrow targeting by 30-40%. Focus on high-intent audiences.';
      actualWaste = spend * 0.35; // 35% waste for overbroad targeting
      isProblematic = true;
      confidence = 82;
    }
    else if (spend > MIN_SPEND && ctr >= config.goodCTR * 1.5 && cpc <= 1.0) {
      causeOfDeath = 'Champion Performance';
      severity = 'CHAMPION';
      diagnosis = `Exceptional ${ctr.toFixed(2)}% CTR with efficient ${cpc.toFixed(2)} CPC.`;
      recommendation = 'Scale budget aggressively (+100%) and duplicate successful elements.';
      confidence = 95;
      isProblematic = false;
    }
    else if (spend < MIN_SPEND) {
      causeOfDeath = 'Learning Phase';
      severity = 'LEARNING';
      diagnosis = `Insufficient data ($${spend.toFixed(2)} spent).`;
      recommendation = `Allow minimum spend of $${MIN_SPEND} for accurate assessment.`;
      isProblematic = false;
    }
    else if (ctr < config.okCTR * 0.5) {
      causeOfDeath = 'Creative Fatigue';
      severity = 'CRITICAL';
      diagnosis = `Very low CTR (${ctr.toFixed(2)}%) indicates creative or targeting issues.`;
      recommendation = 'Refresh creative immediately and review targeting.';
      actualWaste = spend * 0.5; // 50% waste for very poor performance
      isProblematic = true;
      confidence = 90;
    }
    else if (cpc > 5.0) {
      causeOfDeath = 'Cost Inefficiency';
      severity = 'BLEEDING';
      diagnosis = `High CPC (${cpc.toFixed(2)}) burning through budget inefficiently.`;
      recommendation = 'Reduce bids, improve ad relevance, or pause campaign.';
      actualWaste = spend * 0.3; // 30% waste for high costs
      isProblematic = true;
      confidence = 85;
    }
    else {
      causeOfDeath = 'Status Unknown';
      severity = 'INVESTIGATING';
      diagnosis = 'Campaign status unclear. Manual review required.';
      recommendation = 'Check platform directly for current status.';
      confidence = 25;
      isProblematic = false;
    }

    if (isProblematic) {
      problematicCampaigns++;
    }
    
    totalWaste += actualWaste;

    return {
      ...campaign,
      causeOfDeath,
      severity,
      diagnosis,
      recommendation,
      totalLoss: actualWaste.toFixed(2),
      wastePercentage: spend > 0 ? ((actualWaste / spend) * 100).toFixed(0) : 0,
      confidence: confidence.toFixed(0),
      platform: platform.charAt(0).toUpperCase() + platform.slice(1),
      lastActive: campaign.date_stop || campaign.date_start || campaign.updated_time,
      isProblematic,
      searchableId: campaign.campaign_id || campaign.id,
      displayId: campaign.campaign_id || campaign.id,
      actual_status,
      actualWaste
    };
  });

  // Calculate connected accounts (same logic as Campaign Graveyard)
  const uniqueAccountIds = [...new Set(
    campaignList
      .map(c => c.account_id)
      .filter(id => id !== null && id !== undefined && String(id).trim() !== '')
      .map(id => String(id).trim())
  )];
  
  connectedAccounts = uniqueAccountIds.length;
  
  // If no account_id in campaigns, check if we have campaigns (means at least 1 account)
  if (connectedAccounts === 0 && campaignList.length > 0) {
    connectedAccounts = 1;
  }

  return {
    campaigns: analysisResults,
    summary: {
      totalCampaigns: campaignList.length,
      problematicCampaigns,
      totalWaste: Math.round(totalWaste),
      connectedAccounts,
      
      // Additional AI insights
      champions: analysisResults.filter(c => c.severity === 'CHAMPION').length,
      critical: analysisResults.filter(c => c.severity === 'CRITICAL').length,
      bleeding: analysisResults.filter(c => c.severity === 'BLEEDING').length,
      archived: analysisResults.filter(c => c.severity === 'ARCHIVED').length,
      paused: analysisResults.filter(c => c.severity === 'PAUSED').length,
      
      // Waste breakdown
      adFatigueWaste: analysisResults
        .filter(c => c.causeOfDeath === 'Ad Fatigue Detected')
        .reduce((sum, c) => sum + parseFloat(c.actualWaste || 0), 0),
      overbroadWaste: analysisResults
        .filter(c => c.causeOfDeath === 'Overbroad Targeting')
        .reduce((sum, c) => sum + parseFloat(c.actualWaste || 0), 0),
      creativeWaste: analysisResults
        .filter(c => c.causeOfDeath === 'Creative Fatigue')
        .reduce((sum, c) => sum + parseFloat(c.actualWaste || 0), 0),
      costWaste: analysisResults
        .filter(c => c.causeOfDeath === 'Cost Inefficiency')
        .reduce((sum, c) => sum + parseFloat(c.actualWaste || 0), 0),
      
      // AI confidence metrics
      avgConfidence: analysisResults.length > 0 ? 
        Math.round(analysisResults.reduce((sum, c) => sum + parseFloat(c.confidence), 0) / analysisResults.length) : 0,
      highConfidenceDetections: analysisResults.filter(c => parseFloat(c.confidence) >= 85).length
    }
  };
};

// Quick summary function for dashboard use
export const getCampaignSummary = (campaigns) => {
  const analysis = analyzeCampaignWaste(campaigns);
  return analysis.summary;
};

// Get waste breakdown for detailed analysis
export const getWasteBreakdown = (campaigns) => {
  const analysis = analyzeCampaignWaste(campaigns);
  return {
    totalWaste: analysis.summary.totalWaste,
    breakdown: {
      adFatigue: analysis.summary.adFatigueWaste,
      overbroad: analysis.summary.overbroadWaste,
      creative: analysis.summary.creativeWaste,
      cost: analysis.summary.costWaste
    },
    campaigns: analysis.campaigns.filter(c => c.isProblematic)
  };
};